import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { items as itemApi } from '@/api/endpoints';
import { AuthedImage } from '@/components/AuthedImage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/components/ui/link';

/** How many of the most-recently-created items the dashboard strip shows. */
const RECENT_ITEMS_LIMIT = 6;

/**
 * Formats an item's current value for the recent-items strip, mirroring
 * DashboardPage's currency formatting (locale-aware, with a plain-number
 * fallback for a missing currency and a raw "amount code" fallback for one
 * Intl doesn't recognize). Returns null when there's no value to show at
 * all, distinct from "0" which is a real recorded value.
 */
function formatValue(value: number | null, currency: string | null, locale: string) {
  if (value == null) return null;
  if (!currency) return value.toLocaleString();
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

/**
 * Photo+name+value strip of the most recently added items, shown on the
 * dashboard so it reads as "what's actually in my home" rather than a pure
 * stats page. A brand-new account (or one still loading) has nothing recent
 * to show, so this renders nothing rather than an empty strip.
 */
export function RecentItems() {
  const { t, i18n } = useTranslation();
  const recent = useQuery({
    queryKey: ['items', 'recent'],
    queryFn: () => itemApi.search({ size: RECENT_ITEMS_LIMIT, sort: 'createdAt,desc' }),
  });

  // `isSuccess` narrows `recent.data` to a defined Page for TypeScript, so
  // there's no separate undefined-data branch to reason about below.
  if (!recent.isSuccess) return null;

  const recentItems = recent.data.content;
  if (recentItems.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.recentItems')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {recentItems.map((it) => {
            const value = formatValue(it.currentValue, it.currency, i18n.language);
            return (
              <Link key={it.id} to={`/items/${it.id}`} intent="standalone" className="block h-auto w-full rounded-lg p-0">
                <Card className="overflow-hidden transition-shadow hover:shadow-md">
                  {it.thumbnailPath ? (
                    <AuthedImage src={it.thumbnailPath} alt={it.name} className="h-20 w-full object-cover" />
                  ) : (
                    <div className="flex h-20 items-center justify-center bg-muted text-xs text-muted-foreground">
                      {t('items.photos')} —
                    </div>
                  )}
                  <CardContent className="space-y-0.5 p-2">
                    <div className="truncate text-sm font-medium" title={it.name}>
                      {it.name}
                    </div>
                    <div className="text-xs text-muted-foreground">{value ?? '—'}</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
